import { Helmet, HelmetData } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useRef } from "react";
import { useModal } from "../../utils/demoProvider";
import {
  AI_BG_NEW,
  DA_Fetaures_Img,
  DA_NEW_HERO,
  DA_NLP_Img,
  DA_Structure_img,
  Use_Case_Bg,
} from "../../images";
import useIntersectionObserver from "../../utils/useIntersectionObserver";
import { NewFooter } from "../newFooter";

export default function DataAssistantPage() {
  const { openModal } = useModal();
  const targetRef = useRef(null);

  useIntersectionObserver({ targetRef });

  const helmetData = new HelmetData({});

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Data Assistant | AI Analytics Agent for African Enterprises | Analytics Intelligence Africa</title>
        <link rel="canonical" href="https://africa.analyticsintelligence.com/data-assistant" />
        <meta
          name="description"
          content="Data Assistant is an enterprise AI analytics agent that empowers business users across Africa to ask questions in natural language and receive trusted, context-aware insights — no data science expertise required."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence Africa, Data Assistant Africa, AI analytics agent Africa, enterprise AI analytics Africa, natural language analytics Africa, generative AI analytics Africa, KPI analytics AI Africa, data-driven decision making Africa, AI for banking Africa, AI for fintech Africa, NLP analytics Africa, business intelligence AI Africa, data insights Africa"
        />
        <meta property="og:title" content="Data Assistant | AI Analytics Agent for African Enterprises | Analytics Intelligence Africa" />
        <meta property="og:description" content="Ask questions in natural language, get trusted AI-powered analytics insights — Data Assistant by Analytics Intelligence Africa." />
        <meta property="og:url" content="https://africa.analyticsintelligence.com/data-assistant" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Data Assistant | AI Analytics Agent for African Enterprises | Analytics Intelligence Africa" />
        <meta name="twitter:description" content="Ask questions in natural language, get trusted AI-powered analytics insights — Data Assistant by Analytics Intelligence Africa." />
        <meta name="twitter:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Data Assistant",
            "applicationCategory": "BusinessApplication",
            "url": "https://africa.analyticsintelligence.com/data-assistant",
            "description": "An enterprise AI analytics agent that empowers business users to access data, ask questions in natural language, and receive trusted, contextual insights without requiring technical expertise.",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Africa",
              "url": "https://africa.analyticsintelligence.com"
            },
            "operatingSystem": "Cloud",
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock"
            }
          }
        `}</script>
      </Helmet>
      <main>
        {/* Hero Section */}
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center bg-left-bottom md:bg-center"
          ref={targetRef}
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16 md:gap-4 xl:gap-16  pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-4 flex flex-col items-center md:items-start">
                <span className="text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Data Assistant
                </span>
                <h2 className="text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white">
                  An enterprise AI agent for analytics, delivering explainable
                  insights.
                </h2>
                <p className="text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                  Data Assistant is a generative AI analytics assistant that
                  empowers business users to access data, ask questions in
                  natural language, and receive trusted, contextual insights
                  without requiring technical or data science expertise.
                </p>
              </div>

              <button
                onClick={openModal}
                className="text-sm md:text-base font-semibold py-3 w-[170px] text-center bg-[#0173FF] hover:bg-[#0173FF]/70 text-white rounded-[100px] gap-2 transition-colors ease-in duration-300"
              >
                Learn more
              </button>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img className=" w-full" src={DA_NEW_HERO} alt="illustration" />
            </div>
          </div>
        </section>

        {/* Structures Section */}
        <section className="bg-white relative pt-[48px] pb-[96px] md:py-[96px] overflow-hidden">
          <div className="h-[300px] w-[300px] xl:h-[450px] xl:w-[450px] bg-[#7AA8FF] blur-[200px] absolute top-0 -right-[70%] md:-right-[45%] lg:-right-[15%] z-0"></div>
          <div className="container flex flex-col-reverse md:flex-row items-center mt-11 gap-8 relative z-10">
            <div className="flex-1 flex items-center justify-center">
              <img
                src={DA_Structure_img}
                alt="Data Structures"
                className="w-full max-w-[400px] h-auto"
              />
            </div>
            <div className="flex-1 space-y-3">
              <h2 className="text-2xl text-center md:text-left md:text-[32px] font-extrabold text-black">
                Structures
              </h2>

              <div className="space-y-2 text-center md:text-left">
                <h3 className="text-base md:text-lg font-extrabold text-black">
                  Analytics Engine
                </h3>
                <p className="text-sm md:text-base text-black">
                  Analytics processing designed from the ground up for AI
                  reasoning and insight generation, unifying analysis across
                  enterprise datasets, from descriptive exploration to
                  decision-level analytical insights.
                </p>
              </div>
              <div className="space-y-2 text-center md:text-left">
                <h3 className="text-base md:text-lg font-extrabold text-black">
                  Data Language Model
                </h3>
                <p className="text-sm md:text-base text-black">
                  Powered by our Data Language Model (DLM) a fine-tuned small
                  language model developed to interpret and understand user
                  intent from natural language queries with accuracy and domain
                  alignment.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section
          className="bg-[#EDF1FF] py-[48px] bg-left-bottom md:bg-center bg-cover md:bg-conver"
          style={{
            backgroundImage: `url("${Use_Case_Bg}")`,
          }}
        >
          <div className="container relative">
            <div className="flex flex-col md:flex-row items-center mt-11 gap-11 md:gap-5 z-[100] relative">
              <div className="flex-1 space-y-3">
                <h2 className="text-2xl md:text-[32px] text-center md:text-left font-extrabold text-white">
                  Features
                </h2>

                <div className="space-y-2 text-center md:text-left">
                  <h3 className="text-base md:text-lg font-extrabold text-white">
                    Goals Management Engine
                  </h3>
                  <p className="text-sm md:text-base text-white">
                    Leverage generative AI to drive alignment across teams,
                    improve KPI visibility, support accountability, and
                    cultivate a data-driven culture at scale.
                  </p>
                </div>
                <div className="space-y-2 text-center md:text-left">
                  <h3 className="text-base md:text-lg font-extrabold text-white">
                    Personalized Data Analysis
                  </h3>
                  <p className="text-sm md:text-base text-white">
                    Data Assistant provides every user with a personalised
                    analytics assistant that can track performance metrics,
                    respond to analytical queries, and trigger relevant
                    notifications.
                  </p>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <img
                  src={DA_Fetaures_Img}
                  alt="Data Structures"
                  className="w-full md:max-w-[400px] h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* NLP Analytics Section */}
        <section
          className="bg-white relative pt-[48px] pb-[96px] md:pb-[116px]"
          style={{
            // backgroundImage: `url("${Our_Product_Bg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="container">
            <div className="flex flex-col-reverse md:flex-row items-center mt-11 gap-11 md:gap-5">
              <div className="flex-1 flex items-center justify-center">
                <img
                  src={DA_NLP_Img}
                  alt="Data Structures"
                  className="w-full max-w-[400px] h-auto"
                />
              </div>
              <div className="flex-1 flex flex-col items-center md:items-start gap-3 text-center md:text-left">
                <h2 className="text-2xl md:text-[32px] font-extrabold text-black">
                  NLP for Data Analytics
                </h2>
                <p className="text-sm md:text-base text-black">
                  At the foundation of our solution is an advanced Natural
                  Language Processing engine that works alongside the Data
                  Language Model, both enabling seamless conversational
                  interaction with data. Powered by the NLP and DLM systems,
                  Data Assistant understands context and intent to deliver
                  precise, relevant, and explainable answers.
                </p>

                <Link to="/demo">
                  <button className="text-sm md:text-base font-semibold py-3 w-[170px] text-center bg-[#0173FF] hover:bg-[#0173FF]/70 text-white rounded-[100px] gap-2 transition-colors ease-in duration-300">
                    Get a Demo
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <NewFooter />
      </main>
    </>
  );
}
